<template>
  <v-navigation-drawer
    :value="$vuetify.breakpoint.mobile ? navDrawerState : true"
    app
    class="main-nav"
    @input="updateDrawerState"
  >
    <v-sheet color="lighten-4" class="pa-4 text-center">
      <AppUserAvatar :user="user" :size="64"></AppUserAvatar>
      <div class="mt-4 font-nunito font-700" v-if="user">{{ user.friendlyName }}</div>
      <div class="font-nunito font-400 grey--text text--lighten-1">{{ user.companyName }}</div>
    </v-sheet>

    <v-divider></v-divider>
    <v-list>
      <template v-for="(item, idx) in navigationItems">
        <v-tooltip :key="idx" right :disabled="!item.tooltip" color="warning">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-list-item
                :href="item.href"
                :rel="item.href && item.href !== '#' ? 'noopener' : undefined"
                :target="item.href && item.href !== '#' ? '_blank' : undefined"
                :to="item.to"
                :class="[$route.meta.section === item.routeSection ? 'section-active' : '']"
                :disabled="item.disabled"
              >
                <v-list-item-icon>
                  <v-icon :class="item.iconClass" :color="item.iconColor">{{ item.icon }}</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title :class="item.labelClass">
                    {{ item.label }}
                    <v-badge
                      v-if="item.badge"
                      :content="item.badge"
                      :value="item.badge"
                      color="red"
                      offset-x="-4"
                      offset-y="4"
                    >
                    </v-badge>
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-icon v-if="item.newTab">
                  <v-icon color="grey">mdi-open-in-new</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </div>
          </template>
          <span>{{ item.tooltip }}</span>
        </v-tooltip>
      </template>
    </v-list>
    <v-divider></v-divider>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AppUserAvatar from "@/components/AppUserAvatar.vue";
import { TokenService } from "@services";

export default {
  name: "TheMainNav",
  components: { AppUserAvatar },
  computed: {
    ...mapGetters("settings", ["settings"]),
    ...mapGetters("ui", ["navDrawerState"]),
    ...mapGetters("user", ["user"]),
    ...mapGetters("timeClock", ["timeClockDuration"]),

    navigationItems() {
      return this.getNavigationItems();
    },
  },
  props: {
    drawer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      active: false,
    };
  },
  methods: {
    ...mapActions("settings", ["upsertSetting"]),
    ...mapActions("ui", ["navDrawer"]),
    updateDrawerState(state) {
      if (!state) {
        this.navDrawer();
      } else {
        this.navDrawer("open");
      }
    },
    getNavigationItems() {
      return [
        {
          icon: "mdi-view-dashboard-outline",
          label: this.$t("c_menu_full.t_home"),
          to: "/",
          routeSection: "dashboard",
          iconClass: "text-primary",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-clock-time-three-outline",
          label:
            this.timeClockDuration && this.$route.meta.section !== "timeclock"
              ? this.timeClockDuration
              : this.$t("c_menu_full.t_timeclock"),
          to: "/timeclock",
          routeSection: "timeclock",
          iconClass: "text-timeclock",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-camera-outline",
          label: this.$t("c_menu_full.t_log_it"),
          to: "/issues",
          routeSection: "issues",
          iconClass: "text-issues",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-form-select",
          label: this.$t("c_menu_full.t_forms"),
          to: "/forms",
          routeSection: "forms",
          iconClass: "text-forms",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-cube-send",
          label: this.$t("c_menu_full.t_messages"),
          to: "/messages",
          routeSection: "messages",
          iconClass: "text-messages",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-calendar-check",
          label: this.$t("c_menu_full.t_planners"),
          to: "/planners",
          routeSection: "planners",
          iconClass: "text-planners",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-note-multiple-outline",
          label: this.$t("c_menu_full.t_documents"),
          to: "/documents",
          routeSection: "documents",
          iconClass: "text-documents",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-school-outline",
          label: this.$t("c_menu_full.t_training"),
          to: "/training",
          routeSection: "training",
          iconClass: "text-training",
          labelClass: "text-uppercase",
        },
        {
          icon: "mdi-podcast",
          label: "ranesCAST",
          newTab: true,
          href: window.appConfig?.VUE_APP_CAST_WEB_URL
            ? window.appConfig?.VUE_APP_CAST_WEB_URL + "auth?source=app" + `&token=${TokenService.getRefreshToken()}`
            : undefined,
          disabled: !window.appConfig?.VUE_APP_CAST_WEB_URL,
          tooltip: !window.appConfig?.VUE_APP_CAST_WEB_URL ? "ranesCAST is temporarily unavailable" : undefined,
          iconColor: "#e54634",
        },
        {
          icon: "mdi-shield-lock",
          label: "Admin",
          newTab: true,
          href: window.appConfig?.VUE_APP_PORTAL_URL,
          disabled: !window.appConfig?.VUE_APP_PORTAL_URL,
          tooltip: !window.appConfig?.VUE_APP_PORTAL_URL ? "The Admin Portal is temporarily unavailable" : undefined,
          iconColor: "#ff2255",
          labelClass: "text-uppercase admin-label",
        },
      ];
    },
  },
};
</script>
<style lang="scss">
@import "@/scss/variables";

.admin-label {
  color: #999999;
}

.v-list-item--link:before {
  background-color: transparent;
}

.main-nav {
  top: 54px !important;
  height: calc(100vh - 54px) !important;

  // Active state - highlight text with primary color when on matching view
  .section-active {
    .v-list-item__title {
      color: $color-primary !important;
    }
  }
}
</style>
